/**
 * Creature Naming - Three methods to name your creature
 * 1. Mix sounds (syllables)
 * 2. Pick a name (from list)
 * 3. Type a name (text input)
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreatureType, NamingMethod } from './types';
import { CREATURE_CARDS, CREATURE_NAMES, SOUND_SYLLABLES } from './data';

interface CreatureNamingProps {
  language: 'en' | 'es';
  creature: CreatureType;
  onComplete: (name: string) => void;
}

const TEXT = {
  en: {
    title: 'Name Your Creature!',
    subtitle: 'Choose how you want to name your {creature}',
    mixSounds: 'Mix 3 Sounds',
    pickName: 'Pick 3 Words',
    typeName: 'Type a Name',
    mixInstructions: 'Tap 3 sounds to create a name!',
    pickInstructions: 'Pick 3 words to create a funky name!',
    typeInstructions: 'Type a special name and press Enter',
    yourName: 'Your creature name:',
    continue: 'That\'s the name!',
    placeholder: 'Type name here...',
    clear: 'Clear',
    play: 'Play Sound',
    pressEnter: 'Press Enter when done'
  },
  es: {
    title: '¡Nombra tu Criatura!',
    subtitle: 'Elige cómo quieres nombrar tu {creature}',
    mixSounds: 'Mezclar 3 Sonidos',
    pickName: 'Elegir 3 Palabras',
    typeName: 'Escribir Nombre',
    mixInstructions: '¡Toca 3 sonidos para crear un nombre!',
    pickInstructions: '¡Elige 3 palabras para crear un nombre divertido!',
    typeInstructions: 'Escribe un nombre especial y presiona Enter',
    yourName: 'Nombre de tu criatura:',
    continue: '¡Ese es el nombre!',
    placeholder: 'Escribe el nombre aquí...',
    clear: 'Limpiar',
    play: 'Reproducir Sonido',
    pressEnter: 'Presiona Enter cuando termines'
  }
};

export default function CreatureNaming({ language, creature, onComplete }: CreatureNamingProps) {
  const [method, setMethod] = useState<NamingMethod>('pick-name');
  const [mixedSounds, setMixedSounds] = useState<string[]>([]);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [typedName, setTypedName] = useState('');

  const t = TEXT[language];
  const creatureData = CREATURE_CARDS.find(c => c.type === creature)!;
  const syllables = SOUND_SYLLABLES[language];
  const names = CREATURE_NAMES[language];

  // Get current name based on method
  const getCurrentName = (): string => {
    switch (method) {
      case 'mix-sounds':
        return mixedSounds.join('');
      case 'pick-name':
        return selectedWords.join('');
      case 'type-name':
        return typedName;
      default:
        return '';
    }
  };

  const currentName = getCurrentName();
  const canContinue = currentName.length > 0;

  const handleContinue = () => {
    if (canContinue) {
      onComplete(currentName);
    }
  };

  // Mix sounds method - exactly 3 sounds
  const addSyllable = (syllable: string) => {
    if (mixedSounds.length < 3) {
      const newSounds = [...mixedSounds, syllable];
      setMixedSounds(newSounds);

      // Auto-advance when 3 sounds are selected
      if (newSounds.length === 3) {
        const completeName = newSounds.join('');
        setTimeout(() => {
          onComplete(completeName);
        }, 800); // Delay to show the complete name
      }
    }
  };

  const clearMixedSounds = () => {
    setMixedSounds([]);
  };

  // Pick words method - allow selecting up to 3 words
  const toggleWord = (word: string) => {
    if (selectedWords.includes(word)) {
      // Remove if already selected
      setSelectedWords(selectedWords.filter(w => w !== word));
    } else if (selectedWords.length < 3) {
      // Add if less than 3 selected
      const newWords = [...selectedWords, word];
      setSelectedWords(newWords);

      // Auto-advance when 3 words are selected
      if (newWords.length === 3) {
        const completeName = newWords.join('');
        setTimeout(() => {
          onComplete(completeName);
        }, 800); // Delay to show the complete name
      }
    }
  };

  const clearSelectedWords = () => {
    setSelectedWords([]);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center p-6">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-6"
        >
          <div className="text-9xl mb-4">
            {creatureData.emoji}
          </div>

          <h2
            className="text-5xl md:text-7xl mb-2"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 300,
              color: '#2a6d5b',
              letterSpacing: '-0.015em',
            }}
          >
            {t.title}
          </h2>

          <p
            className="text-2xl md:text-3xl mb-6"
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 500,
              color: '#2a6d5b',
            }}
          >
            {t.subtitle.replace('{creature}', creatureData.name[language])}
          </p>

          {/* Name Preview with Blank Spaces */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-block px-8 py-6 mb-4"
            style={{ background: '#f4efde', borderRadius: 24 }}
          >
            <p
              className="mb-3"
              style={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#9a9a9a',
              }}
            >
              {t.yourName}
            </p>

            {method === 'mix-sounds' && (
              <div className="flex gap-3 justify-center">
                {[0, 1, 2].map((index) => (
                  <motion.div
                    key={index}
                    initial={{ scale: 0.85 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    style={{
                      width: 92,
                      height: 92,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 18,
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontSize: 52,
                      fontWeight: 600,
                      transition: 'background 200ms ease, color 200ms ease',
                      background: mixedSounds[index] ? '#42a57a' : '#ffffff',
                      color: mixedSounds[index] ? '#ffffff' : '#c9c0a8',
                      border: mixedSounds[index] ? 'none' : '2px dashed #c9c0a8',
                    }}
                  >
                    {mixedSounds[index] || '_'}
                  </motion.div>
                ))}
              </div>
            )}

            {method === 'pick-name' && (
              <div className="flex gap-2 justify-center flex-wrap">
                {[0, 1, 2].map((index) => (
                  <motion.div
                    key={index}
                    initial={{ scale: 0.85 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    style={{
                      minWidth: 100,
                      padding: '14px 22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 18,
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontStyle: 'italic',
                      fontSize: 32,
                      fontWeight: 600,
                      background: selectedWords[index] ? '#42a57a' : '#ffffff',
                      color: selectedWords[index] ? '#ffffff' : '#c9c0a8',
                      border: selectedWords[index] ? 'none' : '2px dashed #c9c0a8',
                    }}
                  >
                    {selectedWords[index] || '___'}
                  </motion.div>
                ))}
              </div>
            )}

            {method === 'type-name' && (
              <div
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 56,
                  fontWeight: 600,
                  color: '#2a6d5b',
                  minHeight: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {typedName || (
                  <span style={{ color: '#c9c0a8', fontSize: 40 }}>
                    {t.placeholder}
                  </span>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* Method Tabs — outline-green pills, brand-green when active */}
        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          {(['mix-sounds', 'pick-name', 'type-name'] as const).map((m) => {
            const active = method === m;
            const label = m === 'mix-sounds' ? t.mixSounds : m === 'pick-name' ? t.pickName : t.typeName;
            return (
              <button
                key={m}
                onClick={() => setMethod(m)}
                style={{
                  padding: '12px 24px',
                  borderRadius: 999,
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 18,
                  fontWeight: 600,
                  border: '1.5px solid #42a57a',
                  background: active ? '#42a57a' : 'transparent',
                  color: active ? '#ffffff' : '#42a57a',
                  cursor: 'pointer',
                  transition: 'background-color 160ms ease, color 160ms ease',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Method Content */}
        <AnimatePresence mode="wait">
          {/* MIX SOUNDS */}
          {method === 'mix-sounds' && (
            <motion.div
              key="mix-sounds"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-8"
              style={{ background: '#ffffff', border: '1px solid #e8e0c8', borderRadius: 24 }}
            >
              <p
                className="text-center mb-6"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 26,
                  color: '#2a6d5b',
                }}
              >
                {t.mixInstructions}
              </p>

              {/* Syllable Buttons */}
              <div className="grid grid-cols-5 gap-3 mb-6">
                {syllables.map((syllable, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: mixedSounds.length >= 3 ? 1 : 1.05 }}
                    whileTap={{ scale: mixedSounds.length >= 3 ? 1 : 0.95 }}
                    onClick={() => addSyllable(syllable)}
                    disabled={mixedSounds.length >= 3}
                    style={{
                      padding: '18px 12px',
                      background: mixedSounds.length >= 3 ? '#eee8d8' : '#dcecda',
                      color: mixedSounds.length >= 3 ? '#a9a394' : '#2a6d5b',
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontSize: 32,
                      fontWeight: 600,
                      borderRadius: 16,
                      border: 'none',
                      cursor: mixedSounds.length >= 3 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {syllable}
                  </motion.button>
                ))}
              </div>

              {mixedSounds.length > 0 && (
                <button
                  onClick={clearMixedSounds}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    background: 'transparent',
                    color: '#42a57a',
                    border: '1.5px solid #42a57a',
                    borderRadius: 999,
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t.clear}
                </button>
              )}
            </motion.div>
          )}

          {/* PICK NAME */}
          {method === 'pick-name' && (
            <motion.div
              key="pick-name"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-8"
              style={{ background: '#ffffff', border: '1px solid #e8e0c8', borderRadius: 24 }}
            >
              <p
                className="text-center mb-6"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 26,
                  color: '#2a6d5b',
                }}
              >
                {t.pickInstructions}
              </p>

              {/* Name Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {names.map((name, index) => {
                  const isSelected = selectedWords.includes(name);
                  const selectionOrder = selectedWords.indexOf(name) + 1;
                  const canSelect = selectedWords.length < 3 || isSelected;

                  return (
                    <motion.button
                      key={index}
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.04 }}
                      whileHover={{ scale: canSelect ? 1.04 : 1 }}
                      whileTap={{ scale: canSelect ? 0.97 : 1 }}
                      onClick={() => toggleWord(name)}
                      disabled={!canSelect}
                      style={{
                        position: 'relative',
                        padding: '14px 20px',
                        borderRadius: 16,
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontStyle: 'italic',
                        fontSize: 24,
                        fontWeight: 600,
                        border: '1.5px solid #42a57a',
                        background: isSelected ? '#42a57a' : 'transparent',
                        color: isSelected ? '#ffffff' : '#42a57a',
                        opacity: canSelect ? 1 : 0.4,
                        cursor: canSelect ? 'pointer' : 'not-allowed',
                        transition: 'background 160ms ease, color 160ms ease',
                      }}
                    >
                      {isSelected && (
                        <span
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            width: 24,
                            height: 24,
                            background: '#ffffff',
                            color: '#42a57a',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 700,
                            fontFamily: "'Inter', system-ui, sans-serif",
                          }}
                        >
                          {selectionOrder}
                        </span>
                      )}
                      {name}
                    </motion.button>
                  );
                })}
              </div>

              {selectedWords.length > 0 && (
                <button
                  onClick={clearSelectedWords}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    background: 'transparent',
                    color: '#42a57a',
                    border: '1.5px solid #42a57a',
                    borderRadius: 999,
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t.clear}
                </button>
              )}
            </motion.div>
          )}

          {/* TYPE NAME */}
          {method === 'type-name' && (
            <motion.div
              key="type-name"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-8"
              style={{ background: '#ffffff', border: '1px solid #e8e0c8', borderRadius: 24 }}
            >
              <p
                className="text-center mb-6"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 26,
                  color: '#2a6d5b',
                }}
              >
                {t.typeInstructions}
              </p>

              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && typedName.trim().length > 0) {
                    onComplete(typedName.trim());
                  }
                }}
                placeholder={t.placeholder}
                maxLength={20}
                style={{
                  width: '100%',
                  padding: '20px 24px',
                  textAlign: 'center',
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 36,
                  fontWeight: 600,
                  color: '#2a6d5b',
                  background: '#fbfbe8',
                  border: '1.5px solid rgba(42,109,91,0.25)',
                  borderRadius: 18,
                  outline: 'none',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#42a57a'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(42,109,91,0.25)'; }}
                autoFocus
              />

              {typedName.trim().length > 0 && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center mt-4"
                  style={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    color: '#42a57a',
                  }}
                >
                  ↵ {t.pressEnter}
                </motion.p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Continue Button - Only show for type-name method as backup */}
        <div className="text-center mt-8">
          {method === 'type-name' && canContinue && (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleContinue}
              style={{
                padding: '16px 36px',
                background: '#42a57a',
                color: '#ffffff',
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: 22,
                fontWeight: 600,
                borderRadius: 999,
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
              }}
            >
              {t.continue} →
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
}
